import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";

const PrivacyPolicy = () => {
  useEffect(() => {
    document.documentElement.classList.add("privacy-ready");
    window.scrollTo(0, 0);
    return () => {
      document.documentElement.classList.remove("privacy-ready");
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Privacy Policy | SurensInffotek</title>
        <meta
          name="description"
          content="How SurensInffotek collects, uses, shares, and protects personal information."
        />
        <link rel="canonical" href="https://surensinffotek.com/privacy-policy/" />
      </Helmet>

      <main className="bg-white text-slate-800">
        <article className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
          <p className="text-sm font-semibold uppercase tracking-[0.16em] text-sky-600">
            SurensInffotek
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Privacy Policy
          </h1>

          <div className="mt-8 space-y-5 text-base leading-8 text-slate-700">
            <p>
              SurensInffotek has created this Privacy Policy in order to
              demonstrate its commitment in respecting the privacy of its
              User(s) and protecting Your personal information received during
              the use or access of Our Website.
            </p>
            <p>
              We have developed this Privacy Policy for You to understand how
              We collect, communicate and disclose and make use of personal
              information provided by You.
            </p>
          </div>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">
              Collection and the purpose for which information may be collected
            </h2>
            <div className="mt-4 space-y-5 text-base leading-8 text-slate-700">
              <p>
                We collect personal information by lawful and fair means and,
                wherever appropriate, with the knowledge or consent of the
                provider of such data or information.
              </p>
              <p>
                We collect and use Your personal information solely with the
                objective of fulfilling the purpose(s) specified by Us and for
                other compatible purposes only.
              </p>
              <p>
                Before or at the time of collecting personal information, we
                will identify the purposes for which information is being
                collected.
              </p>
              <p>
                You can visit or access Our Website without telling Us who You
                are and without revealing any information about You. However,
                there are times when We would require information from You. Our
                Website may require You to give Us Your contact information
                like Your name, email address, present job details, phone
                number, mobile number, present company, job title or other
                information while filling in certain forms. We expect that the
                personal data or information which You provide shall be
                relevant to the purpose(s) for which it is/was provided, and,
                to the extent necessary for that/those purpose(s), shall be
                accurate, complete, and up-to-date. We assure You that We use
                the information from the forms filled in by You only to send
                You the relevant communication as required by the nature of
                that form or for the purpose(s) for which it was provided. For
                example, if You fill in Your contact details on Our job seekers
                form, We may use Your information to contact You for current
                and prospective job opportunities, recruitment updates etc.
              </p>
              <p>
                If at any point of time, it comes to Our knowledge or We have
                reasonable grounds to believe that the data or the information
                You have provided is inaccurate or false or unsuitable for the
                purpose for which it was intended, We reserve Our right not to
                use the information provided by You for the purpose(s) which
                was intended to be fulfilled by the use of such information.
              </p>
              <p>
                When You access Our Website, information like Your internet
                address, browser type etc. is automatically collected by Our
                servers and is placed in Our Internet access logs. We also make
                use of cookies to intensify Your browsing experience. If You
                have disabled cookies in Your browser, the functionality of the
                Website may be affected and therefore, We advise that You
                enable cookies in Your browser. When You browse through Our
                Website, We analyze the information collected by Our servers to
                identify ways to improve Our Website and make the information
                already available on Our Website more effective. We may also
                use Your information to send You e-mailers to announce the
                launch of Our new services, invitations to Our events and other
                informative e-mails etc. You may opt-out of receiving future
                mailings by choosing the unsubscribe option in the e-mail
                anytime.
              </p>
              <p>
                If You do not wish to provide Your information through the
                relevant form(s) on the Website, You may not fill in the
                relevant form(s). If You wish that We should not collect any
                personal information, You may write to Us and in the event You
                do so, We reserve the discretion to permit access to Our
                Website with such restrictions as the case may be or to
                continue to allow You to use or access the Website.
              </p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">
              Data Security and Retention
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              We protect Your personal information by reasonable security
              safeguards against loss or theft, as well as unauthorized access,
              disclosure, copying, use or modification. However, We do not
              guarantee nor assume any liability for the security and integrity
              of any data or information transmitted by You over the internet,
              including any data or information transmitted via any server
              designated as &quot;secure&quot;. You should not have an
              expectation of privacy in any content, including accounts of
              files transmitted to Our Website through the internet. We claim
              no responsibility for any loss or damage caused to You during the
              transmission of any data or information. We retain Your personal
              information so long as is necessary for the intended purpose(s)
              for which the form(s) was filled in or for the fulfillment of the
              purpose(s) as set out in this Policy and no further.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">
              Third Party Information Sharing
            </h2>
            <div className="mt-4 space-y-5 text-base leading-8 text-slate-700">
              <p>
                This Website may contain links to other sites which are not
                operated or controlled by Us. Our Privacy Policy and procedures
                based herein apply to Our Website and We do not claim any
                responsibility for the privacy practices or the content(s) of
                any such external third party site(s). If You wish to
                understand how Your privacy is protected by such third party
                sites, You may refer to their respective privacy
                policies/privacy statements, data collection and distribution
                policies.
              </p>
              <p>
                In particular, We may provide social media features on Our
                Website which may enable You to share Our information with Your
                social networks and to interact with Us on various social media
                sites like, Twitter, Facebook etc. and the use of these
                features may lead to the collection or sharing of information
                about You. We advise You to review the privacy
                policies/statements and the privacy settings of such social
                networking websites so that You are aware of what personal
                information You are sharing by the use of such links and
                features.
              </p>
              <p>
                Regarding sharing Your data with third parties, We may transfer
                the information We collect from You to Our affiliate(s),
                agents, partners, contractors etc., internationally throughout
                SurensInffotek&apos;s worldwide organization. We take utmost
                care to protect the loss, misuse and alteration of information
                under Our control. We may even ask for Your prior permission
                for the transfer of such data or information whenever required
                and if You deny us the permission, We shall not share Your data
                with any third party. In any case, the data resides behind a
                firewall and the access is restricted to Our authorized
                personnel. In any event, We assure You that We do not trade
                with any individual&apos;s information and the access to Your
                data is provided only to Our employees and trusted partners or
                suppliers or affiliates who are bound with Us under an
                obligation of confidentiality and non-disclosure. We may be
                however compelled to disclose the personal information in the
                event of a legal proceeding, court process, investigation or
                any such other legal process where We are bound to do so by
                applicable laws or where We have to establish or protect Our
                legal rights, intellectual property or where the disclosure is
                required to prevent or take action against any illegal
                activities, including but not limited to suspected or
                apprehended fraud, or where there is an apprehension of any
                situation(s) involving potential threats to the physical safety
                of any person, property etc.
              </p>
            </div>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">
              Accessing, reviewing and/or correction of information
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              At Your request, We shall allow You to review and to correct any
              inaccuracies or amend any deficient data/information, which You
              have shared with Us. However, We do not take responsibility for
              the authenticity of the information or data shared by You nor We
              make any warranties or representation(s) that the information You
              provide is correct, accurate and shall be suitable for the
              purpose(s) for which it is/was provided.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">Anti Spam check</h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              All emails sent to Us are subject to spam check. If We suspect
              that such emails are spam or fraudulent, We reserve Our right to
              reject such emails and initiate such action against the sender
              which may include but may not be limiting to reporting the same
              to the statutory authorities for necessary action.
            </p>
          </section>

          <section className="mt-10">
            <h2 className="text-xl font-semibold text-slate-950">
              Changes to this Policy
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              We are committed to conducting Our business in accordance with
              the principles outlined in the Website Terms and Conditions of
              Use and the Privacy Policy in order to ensure that the
              confidentiality of personal information is protected and
              maintained. We make readily available to all Our Users
              information about Our policies and practices relating to the
              management of personal information and therefore We update Our
              Privacy Policy often and it is Your responsibility to ensure that
              You keep checking Our Privacy Policy for any changes or
              alterations. Please be aware that all revisions and amendments to
              the Privacy Policy shall apply retrospectively.
            </p>
          </section>
        </article>
      </main>
    </>
  );
};

export default PrivacyPolicy;
